import {
  AngleUnit,
  AreaUnit,
  DECIMALS,
  LengthUnit,
  MassUnit,
  UnitSystemType,
  VolumeUnit,
} from '@fulcrum/lib';
import {
  CoordinateSettings,
  ExtendedFormattingSettings,
  FeetInchUnit,
  SlopeMeasurementSettings,
} from '@trimble/types';
import { UnitSettings } from '../models/project-settings';

const DecimalFormats = DECIMALS.map((d) => d.toString());

function formatToDecimalCount(n: string): number {
  return DecimalFormats.indexOf(n);
}

function decimalCountToFormat(n: number): string {
  return DecimalFormats[n];
}

export function mapUnitSettingsToFormatSettings(
  unitSettings: UnitSettings
): ExtendedFormattingSettings {
  let lengthFormatting: 'decimal' | 'fractional' = 'decimal';
  let lengthFractions = 4;
  let lengthMeasurementFormatting: 'decimal' | 'fractional' = 'decimal';
  let lengthMeasurementFractions = 4;
  const fractionSliceStart = 2; // Slice fractional values to extract the denominator value (e.g., "16" from "1/16").
  if (
    unitSettings.lengthUnit === FeetInchUnit &&
    unitSettings.lengthUnitFormat.startsWith('1/')
  ) {
    lengthFractions = parseInt(
      unitSettings.lengthUnitFormat.slice(fractionSliceStart),
      10
    );
    lengthFormatting = 'fractional';
  }
  if (
    unitSettings.lengthMeasurementUnit === FeetInchUnit &&
    unitSettings.lengthMeasurementUnitFormat.startsWith('1/')
  ) {
    lengthMeasurementFractions = parseInt(
      unitSettings.lengthMeasurementUnitFormat.slice(fractionSliceStart),
      10
    );
    lengthMeasurementFormatting = 'fractional';
  }

  return {
    unitSystem: unitSettings.unitSystem as UnitSystemType,
    lengthUnit: unitSettings.lengthUnit as LengthUnit,
    lengthDecimals: formatToDecimalCount(unitSettings.lengthUnitFormat),
    lengthMeasurementUnit: unitSettings.lengthMeasurementUnit as LengthUnit,
    lengthMeasurementDecimals: formatToDecimalCount(
      unitSettings.lengthMeasurementUnitFormat
    ),
    lengthFormatting,
    lengthFractions,
    lengthMeasurementFormatting,
    lengthMeasurementFractions,
    areaUnit: unitSettings.areaUnit as AreaUnit,
    areaDecimals: formatToDecimalCount(unitSettings.areaUnitFormat),
    volumeUnit: unitSettings.volumeUnit as VolumeUnit,
    volumeDecimals: formatToDecimalCount(unitSettings.volumeUnitFormat),
    massUnit: unitSettings.weightUnit as MassUnit,
    massDecimals: formatToDecimalCount(unitSettings.weightUnitFormat),
    angleUnit: unitSettings.angularUnit as AngleUnit,
    angleDecimals: formatToDecimalCount(unitSettings.angularUnitFormat),
    genericDecimals: 3,
    coordinateOrder: mapUnitSettingsToCoordinateSettings(unitSettings),
    slopeMeasurementSettings: mapUnitSettingsToSlopeSettings(unitSettings),
  };
}

export function mapUnitSettingsToSlopeSettings(
  settings: UnitSettings
): SlopeMeasurementSettings {
  return {
    format: settings.slopeUnit,
    displayMode: settings.slopeDisplayMode,
  };
}

export function mapUnitSettingsToCoordinateSettings(
  settings: UnitSettings
): CoordinateSettings {
  return {
    order: settings.coordinateOrder,
  };
}

export function mapFormatSettingsToUnitSettings(
  settings: ExtendedFormattingSettings
): UnitSettings {
  const lengthUnit =
    settings.lengthFormatting === 'decimal'
      ? settings.lengthUnit
      : FeetInchUnit;
  const lengthUnitFormat =
    settings.lengthFormatting === 'decimal'
      ? decimalCountToFormat(settings.lengthDecimals ?? 0)
      : '1/' + (settings.lengthFractions?.toString() ?? '4');
  const lengthMeasurementUnit =
    settings.lengthMeasurementFormatting === 'decimal'
      ? settings.lengthMeasurementUnit
      : FeetInchUnit;
  const lengthMeasurementUnitFormat =
    settings.lengthMeasurementFormatting === 'decimal'
      ? decimalCountToFormat(settings.lengthMeasurementDecimals ?? 0)
      : '1/' + (settings.lengthMeasurementFractions?.toString() ?? '4');

  return {
    unitSystem: settings.unitSystem,
    lengthUnit,
    lengthUnitFormat,
    lengthMeasurementUnit,
    lengthMeasurementUnitFormat,
    areaUnit: settings.areaUnit ?? '',
    areaUnitFormat: decimalCountToFormat(settings.areaDecimals ?? 0),
    volumeUnit: settings.volumeUnit ?? '',
    volumeUnitFormat: decimalCountToFormat(settings.volumeDecimals ?? 0),
    weightUnit: settings.massUnit ?? '',
    weightUnitFormat: decimalCountToFormat(settings.massDecimals ?? 0),
    angularUnit: settings.angleUnit ?? '',
    angularUnitFormat: decimalCountToFormat(settings.angleDecimals ?? 0),
    slopeUnit: settings.slopeMeasurementSettings?.format ?? 'degrees',
    slopeDisplayMode: settings.slopeMeasurementSettings?.displayMode ?? 0,
    coordinateOrder: settings.coordinateOrder?.order ?? 'NEZ',
  };
}
